/**
 * Envío UI-3 · panel "Marca" (panel contextual de la Vista Edición).
 *
 * Muestra y edita la marca asignada al proyecto: la MISMA que lee LMX
 * (tabla brands, vía /api/brands). Sin marca: elegir una de las tuyas o crear
 * una nueva. Lo cualitativo (tono, público, estilo) lo puede completar LMX.
 */
import { useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, Trash2, Upload, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { useBrandStore } from '../../store/brandStore';
import { useWorkflowStore } from '../../store/workflowStore';
import { brandService } from '../../services/brandService';
import { uploadToR2 } from '../../utils/uploadToR2';
import { FONT_LIBRARY } from '../../utils/overlayModel';
import { parseBrandFont } from '../../layout/brandStyle';
import VoicePreviewButton from '../nodes/VoicePreviewButton';
import { ELEVEN_VOICES, voiceText } from '../../utils/elevenVoices';

const ROLES = [
  ['', 'Sin rol'], ['primary', 'Principal'], ['secondary', 'Secundario'], ['accent', 'Acento'],
  ['text', 'Texto'], ['background', 'Fondo'], ['neutral', 'Neutro'],
];
const TEXT_FIELDS = [
  ['tagline', 'Claim'], ['tone', 'Tono'], ['audience', 'Público'],
  ['visual_style', 'Estilo visual'],
];
const MAX_VOICES = 6;
const WEIGHTS = [400, 500, 600, 700, 800, 900];

const fontToParts = (text) => {
  const f = parseBrandFont(text);
  return { family: f?.family || '', weight: f?.weight || 700, raw: f && !f.family ? f.raw : '' };
};
const partsToFont = ({ family, weight }) => (family ? `${FONT_LIBRARY[family].label} ${weight}` : '');

const FontRow = ({ label, value, onChange, testId }) => {
  const p = fontToParts(value);
  return (
    <div className="tl-brand-font">
      <span className="tl-brand-k">{label}</span>
      <select
        value={p.family}
        onChange={(e) => onChange(partsToFont({ family: e.target.value, weight: p.weight }))}
        data-testid={`${testId}-family`}
      >
        <option value="">{p.raw ? `${p.raw} (no disponible en el video)` : 'Elegí…'}</option>
        {Object.entries(FONT_LIBRARY).map(([k, f]) => <option key={k} value={k}>{f.label}</option>)}
      </select>
      <select
        value={p.weight}
        disabled={!p.family}
        onChange={(e) => onChange(partsToFont({ family: p.family, weight: Number(e.target.value) }))}
        data-testid={`${testId}-weight`}
      >
        {WEIGHTS.map((w) => <option key={w} value={w}>{w}</option>)}
      </select>
    </div>
  );
};

/* Envío PJ-1 · voces de la marca: elegir de la lista, escuchar la muestra
   (no gasta créditos) y ajustar velocidad y estabilidad. Es lo mismo que LMX
   pone en los nodos de voz (fields.voices). */
const VoicesEditor = ({ voices, legacy, onChange }) => {
  const list = voices || [];
  const setAt = (i, patch) => onChange(list.map((v, j) => (j === i ? { ...v, ...patch } : v)));
  const add = () => onChange([...list, {
    role: list.length ? `voz ${list.length + 1}` : 'locutor', voice: 'Roger',
    provider: 'elevenlabs', languageCode: 'es',
  }]);
  return (
    <div className="tl-brand-voices" data-testid="tl-brand-voices">
      {!list.length && legacy && (
        <p className="tl-brand-hint">Voz anotada: «{legacy}». Elegila de la lista para poder escucharla y que LMX la aplique sola.</p>
      )}
      {list.map((v, i) => (
        <div className="tl-brand-voice" key={i} data-testid="tl-brand-voice-row">
          <div className="tl-brand-voice-main">
            <input value={v.role || ''} onChange={(e) => setAt(i, { role: e.target.value })} aria-label="Uso de la voz" placeholder="locutor" />
            <select value={v.voice || ''} onChange={(e) => setAt(i, { voice: e.target.value, provider: 'elevenlabs' })}
              aria-label="Voz" data-testid={`tl-brand-voice-select-${i}`}>
              {v.voice && !ELEVEN_VOICES.includes(v.voice) && <option value={v.voice}>{v.voice}</option>}
              {ELEVEN_VOICES.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            {ELEVEN_VOICES.includes(v.voice) && <VoicePreviewButton voice={v.voice} testId={`tl-brand-voice-preview-${i}`} />}
            <button type="button" className="tl-icon-btn" onClick={() => onChange(list.filter((_, j) => j !== i))} aria-label="Quitar voz"><Trash2 size={13} /></button>
          </div>
          <div className="tl-brand-voice-sliders">
            <label><span>Velocidad {(v.speed ?? 1).toFixed(2)}</span>
              <input type="range" min="0.7" max="1.2" step="0.05" value={v.speed ?? 1} onChange={(e) => setAt(i, { speed: Number(e.target.value) })} />
            </label>
            <label><span>Estabilidad {(v.stability ?? 0.5).toFixed(2)}</span>
              <input type="range" min="0" max="1" step="0.05" value={v.stability ?? 0.5} onChange={(e) => setAt(i, { stability: Number(e.target.value) })} />
            </label>
          </div>
        </div>
      ))}
      {list.length < MAX_VOICES && (
        <button type="button" className="tl-brand-btn" onClick={add} data-testid="tl-brand-add-voice"><Plus size={13} /> Agregar voz</button>
      )}
    </div>
  );
};

/* Envío PJ-2 · el editor de la marca, separado del panel para usarlo también
   en la Biblioteca (edita CUALQUIER marca, no sólo la del proyecto). Recibe la
   marca y cómo guardarla; el panel le agrega sus acciones de proyecto. */
export const BrandEditor = ({ brand, logoUrl, update, undo, actions = null, hint = null }) => {
  const [draft, setDraft] = useState(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setDraft(brand ? JSON.parse(JSON.stringify(brand.fields || {})) : null);
    setName(brand?.name || '');
  }, [brand]);

  const dirty = useMemo(() => brand && draft
    && (JSON.stringify(draft) !== JSON.stringify(brand.fields || {}) || name.trim() !== brand.name),
  [brand, draft, name]);

  if (!brand || !draft) return null;
  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const colors = draft.colors || [];
  const setColor = (i, patch) => setField('colors', colors.map((c, j) => (j === i ? { ...c, ...patch } : c)));

  const run = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); if (okMsg) toast.success(okMsg); } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo completar');
    } finally { setBusy(false); }
  };

  const save = () => run(async () => {
    const clean = { ...draft, colors: colors.map(({ hex, name: n, role }) => ({ hex, ...(n ? { name: n } : {}), ...(role ? { role } : {}) })) };
    // Envío PJ-1 · `voice` (texto) queda como resumen de las voces elegidas.
    if (draft.voices?.length) clean.voice = voiceText(draft.voices);
    else if (brand.fields?.voices?.length) { clean.voices = []; clean.voice = null; }
    await update({ name: name.trim() || brand.name, fields: clean });
  }, 'Marca guardada. LMX ya usa estos datos.');

  const uploadLogo = (file) => file && run(async () => {
    const res = await uploadToR2(file);
    if (!res.assetId) throw new Error('sin asset');
    await update({ logo_asset_id: res.assetId });
  }, 'Logo actualizado');

  const colorsFromLogo = () => run(async () => {
    if (!brand?.logo_asset_id) throw new Error('sin logo');
    const pal = await brandService.palette(brand.logo_asset_id, 6);
    if (!pal.length) throw new Error('sin colores');
    setField('colors', pal.map((p, i) => ({ hex: p.hex, ...(i === 0 ? { role: 'primary' } : {}) })));
  }, 'Colores sacados del logo. Revisalos y tocá Guardar.');

  return (
    <div className="tl-brand-body">
      <div className="tl-brand-logo">
        {logoUrl ? <img src={logoUrl} alt={`Logo de ${brand.name}`} /> : <span>Sin logo</span>}
        <div className="tl-brand-logo-actions">
          <label className="tl-brand-btn">
            <Upload size={13} /> {logoUrl ? 'Cambiar logo' : 'Subir logo'}
            <input type="file" accept="image/*" hidden onChange={(e) => { uploadLogo(e.target.files?.[0]); e.target.value = ''; }} data-testid="tl-brand-logo-input" />
          </label>
          {brand.logo_asset_id && (
            <button type="button" className="tl-brand-btn" onClick={colorsFromLogo} disabled={busy} data-testid="tl-brand-palette">
              <Wand2 size={13} /> Colores del logo
            </button>
          )}
        </div>
      </div>

      <label className="tl-inspector-field wide"><span>Nombre</span>
        <input value={name} onChange={(e) => setName(e.target.value)} data-testid="tl-brand-name" />
      </label>

      <div className="tl-inspector-subhead">Colores</div>
      <div className="tl-brand-colors" data-testid="tl-brand-colors">
        {colors.map((c, i) => (
          <div className="tl-brand-color" key={`${i}-${c.hex}`}>
            <input type="color" value={c.hex} onChange={(e) => setColor(i, { hex: e.target.value.toUpperCase() })} aria-label={`Color ${i + 1}`} />
            <code>{c.hex}</code>
            <select value={c.role || ''} onChange={(e) => setColor(i, { role: e.target.value || undefined })} aria-label="Uso del color">
              {ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <button type="button" className="tl-icon-btn" onClick={() => setField('colors', colors.filter((_, j) => j !== i))} aria-label="Quitar color"><Trash2 size={13} /></button>
          </div>
        ))}
        {colors.length < 8 && (
          <button type="button" className="tl-brand-btn" onClick={() => setField('colors', [...colors, { hex: '#FFFFFF' }])} data-testid="tl-brand-add-color">
            <Plus size={13} /> Agregar color
          </button>
        )}
      </div>

      <div className="tl-inspector-subhead">Tipografías <em>(el video usa Inter, Montserrat, Bebas o Playfair)</em></div>
      <FontRow label="Títulos" value={draft.fonts?.titles} testId="tl-brand-font-titles"
        onChange={(v) => setField('fonts', { ...(draft.fonts || {}), titles: v })} />
      <FontRow label="Textos" value={draft.fonts?.body} testId="tl-brand-font-body"
        onChange={(v) => setField('fonts', { ...(draft.fonts || {}), body: v })} />

      <div className="tl-inspector-subhead">Identidad</div>
      {TEXT_FIELDS.map(([k, label]) => (
        <label className="tl-inspector-field wide" key={k}><span>{label}</span>
          <textarea rows={k === 'tagline' ? 1 : 2} value={draft[k] || ''}
            onChange={(e) => setField(k, e.target.value)} data-testid={`tl-brand-${k}`} />
        </label>
      ))}

      <div className="tl-inspector-subhead">Voces <em>(LMX las usa en los nodos de voz)</em></div>
      <VoicesEditor voices={draft.voices} legacy={draft.voice}
        onChange={(v) => setField('voices', v)} />

      <div className="tl-brand-actions">
        <button type="button" className="tl-brand-save" disabled={!dirty || busy} onClick={save} data-testid="tl-brand-save">
          {busy ? <Loader2 size={13} className="spin" /> : null} Guardar
        </button>
        {typeof actions === 'function' ? actions({ busy, dirty, run }) : actions}
        {/* Envío LMX-4c · cada edición guarda la versión anterior */}
        <button type="button" className="tl-brand-link" disabled={busy || dirty}
          title={dirty ? 'Guardá o descartá los cambios antes de deshacer' : 'Volver la marca a como estaba antes del último cambio'}
          onClick={() => run(() => undo(), 'Marca restaurada a la versión anterior')}
          data-testid="tl-brand-undo">Deshacer último cambio</button>
      </div>
      {hint && <p className="tl-brand-hint">{hint}</p>}
    </div>
  );
};

const BrandPanel = () => {
  const projectId = useWorkflowStore((s) => s.currentProjectId);
  const { brand, logoUrl, brands, loading, load, loadList, assign, create, update, undo } = useBrandStore();
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  const [newName, setNewName] = useState('');

  useEffect(() => { if (projectId) load(projectId); }, [projectId, load]);
  useEffect(() => { if (!brand || picking) loadList(); }, [brand, picking, loadList]);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); if (okMsg) toast.success(okMsg); } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo completar');
    } finally { setBusy(false); }
  };

  if (!projectId) {
    return (
      <aside className="tl-inspector tl-brand-panel" data-testid="tl-brand-panel">
        <div className="tl-inspector-header"><span className="tl-inspector-title">Marca</span></div>
        <p className="tl-brand-empty">Guardá el proyecto para asignarle una marca.</p>
      </aside>
    );
  }

  const showPicker = !brand || picking;

  return (
    <aside className="tl-inspector tl-brand-panel" data-testid="tl-brand-panel" aria-label="Marca del proyecto">
      <div className="tl-inspector-header">
        <span className="tl-inspector-title">Marca{brand ? ` · @${brand.handle}` : ''}</span>
      </div>

      {loading && <div className="tl-brand-empty"><Loader2 size={16} className="spin" /> Cargando…</div>}

      {!loading && showPicker && (
        <div className="tl-brand-pick" data-testid="tl-brand-picker">
          <p className="tl-brand-empty">
            {brand ? 'Elegí otra marca para este proyecto:' : 'Este proyecto no tiene marca. Elegí una de las tuyas:'}
          </p>
          {(brands || []).map((b) => (
            <button
              key={b.id}
              type="button"
              className={`tl-brand-option ${brand?.id === b.id ? 'active' : ''}`}
              disabled={busy}
              onClick={() => run(async () => { await assign(projectId, b.id); setPicking(false); }, `Marca @${b.handle} asignada`)}
              data-testid="tl-brand-option"
            >
              <span className="tl-brand-dots">{(b.colors || []).slice(0, 4).map((hx) => <i key={hx} style={{ background: hx }} />)}</span>
              <span>{b.name}</span>
            </button>
          ))}
          {brands && brands.length === 0 && <p className="tl-brand-empty">Todavía no tenés marcas.</p>}
          <div className="tl-brand-new">
            <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Nombre de una marca nueva" data-testid="tl-brand-new-name" />
            <button
              type="button"
              disabled={busy || !newName.trim()}
              onClick={() => run(async () => { await create(projectId, newName.trim()); setNewName(''); setPicking(false); }, 'Marca creada y asignada')}
              data-testid="tl-brand-create"
            >
              <Plus size={13} /> Crear
            </button>
          </div>
          <p className="tl-brand-hint">También podés pedírsela a LMX: «creá la marca de mi cliente con este logo» y adjuntá la imagen.</p>
          {brand && <button type="button" className="tl-brand-link" onClick={() => setPicking(false)}>Volver</button>}
        </div>
      )}

      {!loading && brand && !picking && (
        <BrandEditor
          brand={brand}
          logoUrl={logoUrl}
          update={update}
          undo={undo}
          hint="Para aplicarla, seleccioná un texto y tocá «Aplicar estilo de marca»."
          actions={({ busy: editing, run: runEdit }) => (
            <>
              <button type="button" className="tl-brand-link" onClick={() => setPicking(true)} data-testid="tl-brand-change">Cambiar de marca</button>
              <button type="button" className="tl-brand-link" disabled={editing}
                onClick={() => runEdit(() => assign(projectId, null), 'El proyecto quedó sin marca')}>Quitar del proyecto</button>
            </>
          )}
        />
      )}
    </aside>
  );
};

export default BrandPanel;
